import React from "react";
import { Player, SavedTournament } from "../types.ts";
import {
  ResultMatrix,
  calculatePoints,
  calculateRankings,
  combineRounds,
  formatResult,
  formatTime,
} from "../utils/tournament-calc.ts";
import { formatArchiveValue, getArchiveColumns } from "../utils/ranking.ts";
import { getParticipantCount } from "../utils/championship.ts";

interface TournamentResultTableProps {
  tournament: SavedTournament;
  /** Záloha pro starší turnaje, které nemají uložená jména a časy. */
  allPlayers: Player[];
}

const parseMatrix = (raw: unknown): ResultMatrix => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as ResultMatrix;
  try {
    const parsed = JSON.parse(String(raw));
    return Array.isArray(parsed) ? (parsed as ResultMatrix) : [];
  } catch (e) {
    console.error("Nepodařilo se načíst výsledky turnaje:", e);
    return [];
  }
};

/**
 * Barva buňky podle celkového skóre proti danému soupeři – hodnota je
 * vždy i vypsaná, barva ji jen doprovází. `max` je 1 u jednokolového
 * turnaje a 2 u dvoukolového, aby remíza vycházela na polovinu.
 */
const resultCellClass = (
  value: number | null | undefined,
  max: number
): string => {
  if (value === null || value === undefined) return "bg-white text-slate-400";
  if (value > max / 2) return "bg-emerald-50 text-emerald-700";
  if (value === max / 2) return "bg-amber-50 text-amber-700";
  return "bg-rose-50 text-rose-600";
};

const rankBadgeClass = (rank: number | undefined): string => {
  if (rank === 1) return "bg-amber-400 text-amber-950";
  if (rank === 2) return "bg-slate-300 text-slate-800";
  if (rank === 3) return "bg-orange-300 text-orange-950";
  return "bg-slate-100 text-slate-600";
};

/**
 * Výsledková křížová tabulka odehraného turnaje. Pouze pro čtení –
 * používá ji veřejná stránka.
 */
const TournamentResultTable: React.FC<TournamentResultTableProps> = ({
  tournament,
  allPlayers,
}) => {
  const matrix = parseMatrix(tournament.results);
  // Dvoukolový turnaj má odvety v samostatné matici
  const matrix2 = tournament.doubleRound
    ? parseMatrix(tournament.results2)
    : null;
  const isDoubleRound = Boolean(matrix2 && matrix2.length > 0);
  const playerCount = getParticipantCount(tournament) || matrix.length;

  const findPlayer = (index: number): Player | undefined => {
    const id = tournament.player?.[index];
    if (!id) return undefined;
    return allPlayers.find((p) => p.id === id);
  };

  const getName = (index: number): string =>
    tournament.playerNames?.[index] ||
    findPlayer(index)?.player_name ||
    `${index + 1}`;

  const getTime = (index: number): number | null => {
    const saved = tournament.times?.[index];
    if (saved !== undefined && saved !== null) return saved;
    return findPlayer(index)?.player_handicap ?? null;
  };

  // Uložené hodnoty mají přednost, dopočet je pro starší turnaje
  const scoreMatrix = combineRounds(matrix, matrix2, playerCount);

  const points =
    tournament.points && tournament.points.length
      ? tournament.points
      : calculatePoints(scoreMatrix, playerCount);
  // Pomocná hodnocení podle režimu, se kterým byl turnaj uzavřen
  const archiveColumns = getArchiveColumns(
    tournament,
    matrix2 && matrix2.length ? [matrix, matrix2] : [matrix],
    playerCount
  );
  const rankings: { [key: string]: number } =
    tournament.rankings && Object.keys(tournament.rankings).length
      ? tournament.rankings
      : (calculateRankings(scoreMatrix, playerCount) as {
          [key: string]: number;
        });

  const hasNewTimes = Boolean(
    tournament.newTimes && tournament.newTimes.some((t) => t !== null)
  );

  if (matrix.length === 0) {
    return (
      <p className="text-slate-500 py-6 text-center">
        Pro tento turnaj nejsou uložené výsledky.
      </p>
    );
  }

  // Řádky seřazené podle umístění, ne podle pozice v tabulce
  const order = Array.from({ length: playerCount }, (_, i) => i).sort(
    (a, b) => (Number(rankings[a]) || 99) - (Number(rankings[b]) || 99)
  );

  const headCell =
    "px-2 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500";

  return (
    <div className="overflow-x-auto -mx-1 px-1">
      <table className="min-w-full border-separate border-spacing-0 text-sm tabular-nums">
        <thead>
          <tr>
            <th className={`${headCell} text-center w-12`}>Poř.</th>
            <th className={`${headCell} text-left`}>Hráč</th>
            <th className={`${headCell} text-center`}>Čas</th>
            {hasNewTimes && (
              <th className={`${headCell} text-center`}>Nový čas</th>
            )}
            {order.map((index) => (
              <th
                key={index}
                className={`${headCell} text-center ${
                  isDoubleRound ? "w-16 min-w-16" : "w-9 min-w-9"
                }`}
                title={getName(index)}
              >
                {Number(rankings[index]) || index + 1}
              </th>
            ))}
            <th className={`${headCell} text-center`}>Body</th>
            {archiveColumns.map((column) => (
              <th
                key={column.key}
                className={`${headCell} text-center`}
                title={column.full}
              >
                {column.short}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {order.map((rowIndex) => {
            const rank = Number(rankings[rowIndex]);
            return (
              <tr key={rowIndex} className="hover:bg-slate-50">
                <td className="px-2 py-2 text-center border-t border-slate-100">
                  <span
                    className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${rankBadgeClass(
                      rank
                    )}`}
                  >
                    {rank || "–"}
                  </span>
                </td>
                <td className="px-2 py-2 border-t border-slate-100 font-medium text-slate-800 whitespace-nowrap">
                  {getName(rowIndex)}
                </td>
                <td className="px-2 py-2 text-center border-t border-slate-100 text-slate-600">
                  {formatTime(getTime(rowIndex))}
                </td>
                {hasNewTimes && (
                  <td className="px-2 py-2 text-center border-t border-slate-100 text-slate-600">
                    {formatTime(tournament.newTimes?.[rowIndex])}
                  </td>
                )}
                {order.map((colIndex) => {
                  const value =
                    rowIndex === colIndex
                      ? null
                      : scoreMatrix[rowIndex]?.[colIndex] ?? null;
                  return (
                    <td
                      key={colIndex}
                      className="px-0.5 py-1 text-center border-t border-slate-100"
                    >
                      {isDoubleRound ? (
                        // Každá partie má vlastní čtvereček a vlastní barvu,
                        // oddělené mezerou – hned je vidět obojí zvlášť
                        <span
                          className="flex h-7 w-[3.75rem] mx-auto gap-0.5"
                          title={
                            rowIndex === colIndex
                              ? undefined
                              : `${getName(rowIndex)} – ${getName(
                                  colIndex
                                )}: první partie a odveta`
                          }
                        >
                          {rowIndex === colIndex ? (
                            <span className="flex-1 rounded bg-slate-200" />
                          ) : (
                            <>
                              <span
                                className={`flex-1 flex items-center justify-center rounded font-bold ${resultCellClass(
                                  matrix[rowIndex]?.[colIndex],
                                  1
                                )}`}
                              >
                                {formatResult(matrix[rowIndex]?.[colIndex])}
                              </span>
                              <span
                                className={`flex-1 flex items-center justify-center rounded font-bold ${resultCellClass(
                                  matrix2?.[rowIndex]?.[colIndex],
                                  1
                                )}`}
                              >
                                {formatResult(matrix2?.[rowIndex]?.[colIndex])}
                              </span>
                            </>
                          )}
                        </span>
                      ) : (
                        <span
                          className={`flex h-7 w-7 mx-auto items-center justify-center rounded font-bold ${
                            rowIndex === colIndex
                              ? "bg-slate-200 text-slate-200"
                              : resultCellClass(value, isDoubleRound ? 2 : 1)
                          }`}
                          title={
                            rowIndex === colIndex
                              ? undefined
                              : `${getName(rowIndex)} – ${getName(colIndex)}`
                          }
                        >
                          {rowIndex === colIndex ? "" : formatResult(value)}
                        </span>
                      )}
                    </td>
                  );
                })}
                <td className="px-2 py-2 text-center border-t border-slate-100 font-bold text-slate-900">
                  {points[rowIndex] !== undefined ? points[rowIndex] : ""}
                </td>
                {archiveColumns.map((column) => (
                  <td
                    key={column.key}
                    className="px-2 py-2 text-center border-t border-slate-100 text-slate-500"
                  >
                    {formatArchiveValue(column, rowIndex)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      <p className="mt-3 text-xs text-slate-400">
        Sloupce s čísly odpovídají pořadí hráčů. Výhra = 1, remíza = ½, prohra
        = 0.
        {isDoubleRound
          ? " Turnaj se hrál dvoukolově – v buňce je první partie a odveta."
          : ""}
      </p>
    </div>
  );
};

export default TournamentResultTable;
